import classNames from 'classnames'
import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'
import { EFormSource } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestDetail/actionView/dataCollector/FormSelector'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import React, { useMemo, useState } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { Button, IButtonProps } from 'core-components/library/button'
import {
  DatePicker,
  IDatePickerMultiple,
  IDatePickerRange,
  IDatePickerSingle,
} from 'core-components/library/datepicker'
import { DropdownDynamic } from 'core-components/library/dropdown'
import { IDropdownDynamicProps } from 'core-components/library/dropdown/DropdownDynamic'
import { IInputProps, Input } from 'core-components/library/inputs'
import { LoaderOverlay } from 'core-components/library/loader'
import { Controller } from 'core-components/library/validation'
import { ValidateInput } from 'core-components/library/validation'
import { IValidatorProps } from 'core-components/library/validation'
import { IFormCollector } from 'core/classes/models/IFormCollector'
import {
  IAudienceCondition,
  VisualAudienceExtended,
} from 'core/components/shared/ui/VisualAudienceExtended'
import { CrudStore } from 'core/types/crudStore'
import { refreshContractSignatureProgressData } from 'onboarding/features/actions/refreshProgressData'
import { DateHelper } from 'onboarding/utils/dateHelper'

import { useContractData } from './hooks/useContractData'
import { ReadOnlyField } from './ReadOnlyField'

import styles from './ContractData.module.scss'

const mandatoryFields = [
  'ContractStatus',
  'PartnerSignature',
  'EffectiveDate',
  'Activated',
  'ActivatedBy',
  'IssueOutDate',
]

interface IProps {
  requestId: string
  audienceSettings: IAudienceCondition
}

interface IDatePickerData {
  field: string
  label: string
  required: boolean
}

const datePickersArray: IDatePickerData[] = [
  { field: 'IssueOutDate', label: 'Agreement issue out date', required: true },
  { field: 'ReIssueDate', label: 'Agreement re-issue date', required: false },
  { field: 'AgreementReject', label: 'Agreement reject date', required: false },
  { field: 'ScannedCopyReceived', label: 'Scanned copy received date', required: false },
  { field: 'OrigCopyReceived', label: 'Original copy received date', required: false },
  { field: 'PartnerSignature', label: 'Partner signature date', required: true },
  { field: 'HPSignature', label: 'HP signature date', required: false },
  { field: 'EffectiveDate', label: 'Effective date', required: true },
  { field: 'Activated', label: 'Activated Date', required: true },
]

const calcCompleteness = (obj: IObjectAny) => {
  if (obj.ContractStatus === 'Completed' || obj.ContractStatus === 'Cancelled') {
    return 100
  }

  const completedCount = mandatoryFields.reduce((count, field) => {
    return obj[field] ? count + 1 : count
  }, 0)

  return ((completedCount / mandatoryFields.length) * 100).toFixed(2)
}

const checkFn = (item: IObjectAny, field: string) => async (value: string) => {
  const completeness = calcCompleteness({ ...item, [field]: value })
  if (completeness === 100) {
    return ''
  }
  if (!value) {
    return 'Shouldn`t be empty'
  }
  return ''
}

export const ContractData = (props: IProps) => {
  const dispatch = useAppDispatch()
  const [item, setItem] = useState<IObjectAny>({})
  const [wip, setWip] = useState(true)
  const source = CrudStore.get(CrudStoreItemOnboarding.ContractsFormData)
  const { cruds } = useContractData(
    (value) => {
      setItem(value)
      setWip(false)
    },
    props.requestId,
    source
  )
  const completenessFormsSource = CrudStore.get<IFormCollector>(
    CrudStoreItem.FormCollector,
    'data_collector_table'
  )

  const checkFnValueDeps = useMemo(() => [item], [item])

  const onSave = async () => {
    setWip(true)
    const formData: IForm | null = await source.getById({
      id: props.requestId,
      force: true,
      keyField: 'RequestId',
    })
    const itemToUpdateOrCreate = formData ? { ...formData, ...item } : item
    await source[formData ? 'update' : 'create'](EFormSource.ContractFollowUp, [
      {
        ...itemToUpdateOrCreate,
        Completeness: calcCompleteness(itemToUpdateOrCreate),
        RequestId: props.requestId,
      },
    ])
    dispatch(refreshContractSignatureProgressData())
    setWip(false)
    completenessFormsSource.triggerOnChange()
  }
  const onChange = (field: string) => (value: string | number) => {
    setItem({ ...item, [field]: value })
  }

  const checkFns = useMemo(() => {
    return {
      activatedBy: (value: string) => checkFn(item, 'ActivatedBy')(value),
      hpSigner1: (value: string) => checkFn(item, 'HPSigner1')(value),
      partnerSigner1: (value: string) => checkFn(item, 'PartnerSigner1')(value),
    }
  }, [item])

  const writeAudience = props.audienceSettings

  return (
    <Controller>
      <div className={classNames(styles.row, styles.content)}>
        <LoaderOverlay show={wip} />
        <div className={styles.col}>
          <div className={styles.bottomWrapper}>
            <ReadOnlyField label={'Current Contract Status'} value={item.ContractStatus || null} />
          </div>
          <VisualAudienceExtended<IDropdownDynamicProps>
            write={writeAudience}
            component={DropdownDynamic}
            margin={'md'}
            label={'Business Status'}
            source={cruds.businessStatuses}
            value={item.BusinessStatus}
            caption={'en'}
            onChange={(_, item) => onChange('BusinessStatus')(item.value)}
          />
          <VisualAudienceExtended<IDropdownDynamicProps>
            write={writeAudience}
            component={DropdownDynamic}
            margin={'md'}
            label={'Production Agreement'}
            source={cruds.agreementStatuses}
            value={item.Agreement}
            caption={'en'}
            onChange={(_, item) => onChange('Agreement')(item.value)}
          />
          {datePickersArray.map((el, index) => (
            <VisualAudienceExtended<IDatePickerSingle | IDatePickerRange | IDatePickerMultiple>
              key={index}
              margin={'md'}
              write={writeAudience}
              component={DatePicker}
              mode={'single'}
              value={
                item[el.field]
                  ? DateHelper.convertServerISOToDate(item[el.field]) || undefined
                  : undefined
              }
              onChange={(value: Date) => {
                let formattedDate = ''
                if (value) {
                  formattedDate = DateHelper.convertDateToServerISO(value)
                }
                onChange(el.field)(formattedDate)
              }}
              label={el.label}
              required={el.required}
            />
          ))}
          <VisualAudienceExtended<IInputProps & IValidatorProps>
            write={writeAudience}
            component={ValidateInput}
            label={'Activated By'}
            margin={'md'}
            value={item.ActivatedBy}
            onChange={onChange('ActivatedBy')}
            checkFn={checkFns.activatedBy}
            valueDependencies={checkFnValueDeps}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'Relationship'}
            margin={'md'}
            value={item.Relationship}
            onChange={onChange('Relationship')}
          />
        </div>
        <div className={styles.col}>
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'Agreement Number'}
            margin={'md'}
            value={item.AgreementNumber}
            onChange={onChange('AgreementNumber')}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'Buyer Type'}
            margin={'md'}
            value={item.BuyerType}
            onChange={onChange('BuyerType')}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'HP Contract Type'}
            margin={'md'}
            value={item.HPContractType}
            onChange={onChange('HPContractType')}
          />
          <VisualAudienceExtended<IInputProps & IValidatorProps>
            write={writeAudience}
            component={ValidateInput}
            label={'HP Signer 1'}
            margin={'md'}
            value={item.HPSigner1}
            onChange={onChange('HPSigner1')}
            checkFn={checkFns.hpSigner1}
            valueDependencies={checkFnValueDeps}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'HP Signer 2'}
            margin={'md'}
            value={item.HPSigner2}
            onChange={onChange('HPSigner2')}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'HP Signer Title 1'}
            margin={'md'}
            value={item.HPSignerTitle1}
            onChange={onChange('HPSignerTitle1')}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'HP Signer Title 2'}
            margin={'md'}
            value={item.HPSignerTitle2}
            onChange={onChange('HPSignerTitle2')}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'No of HP Legal Entity'}
            margin={'md'}
            value={item.NoHPLegalEntity}
            onChange={onChange('NoHPLegalEntity')}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'HP Legal Entity'}
            margin={'md'}
            value={item.HPLegalEntity}
            onChange={onChange('HPLegalEntity')}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'HP Legal Entity Type'}
            margin={'md'}
            value={item.HPLegalEntityType}
            onChange={onChange('HPLegalEntityType')}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'HP Legal Entity 2'}
            margin={'md'}
            value={item.HPLegalEntity2}
            onChange={onChange('HPLegalEntity2')}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'HP Legal Entity Type 2'}
            margin={'md'}
            value={item.HPLegalEntityType2}
            onChange={onChange('HPLegalEntityType2')}
          />
          <VisualAudienceExtended<IInputProps & IValidatorProps>
            component={ValidateInput}
            write={writeAudience}
            label={'Partner Signer 1'}
            margin={'md'}
            value={item.PartnerSigner1}
            onChange={onChange('PartnerSigner1')}
            checkFn={checkFns.partnerSigner1}
            valueDependencies={checkFnValueDeps}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'Partner Signer 2'}
            margin={'md'}
            value={item.PartnerSigner2}
            onChange={onChange('PartnerSigner2')}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'Partner Tier'}
            margin={'md'}
            value={item.PartnerTier}
            onChange={onChange('PartnerTier')}
          />
          <VisualAudienceExtended<IInputProps>
            write={writeAudience}
            component={Input}
            label={'Record Type'}
            margin={'md'}
            value={item.RecordType}
            onChange={onChange('RecordType')}
          />
          <div className={styles.btnWrapper}>
            <VisualAudienceExtended<IButtonProps>
              write={writeAudience}
              component={Button}
              onClick={onSave}
            >
              Save
            </VisualAudienceExtended>
          </div>
        </div>
      </div>
    </Controller>
  )
}
